// Keyboard shortcut hook — vim/Gmail "g X" page navigation plus `?` for help.
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ShortcutNavTarget {
  key: string;
  label: string;
  path: string;
}

export const NAV_SHORTCUTS: ShortcutNavTarget[] = [
  { key: 't', label: 'Routines', path: '/routines' },
  { key: 'e', label: 'Email', path: '/email' },
  { key: 'c', label: 'Contacts', path: '/contacts' },
  { key: 'a', label: 'Agents', path: '/agents' },
  { key: 'k', label: 'Knowledge', path: '/knowledge' },
  { key: 'y', label: 'Analytics', path: '/analytics' },
  { key: 's', label: 'Settings', path: '/settings' },
];

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return false;
}

interface UseKeyboardShortcutsOptions {
  onShowHelp: () => void;
}

export function useKeyboardShortcuts({ onShowHelp }: UseKeyboardShortcutsOptions): void {
  const navigate = useNavigate();

  useEffect(() => {
    let gPressed = false;
    let gTimer: number | null = null;

    const clearG = (): void => {
      gPressed = false;
      if (gTimer !== null) {
        window.clearTimeout(gTimer);
        gTimer = null;
      }
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;

      if (e.key === '?' || (e.key === '/' && e.shiftKey)) {
        e.preventDefault();
        clearG();
        onShowHelp();
        return;
      }

      if (gPressed) {
        const match = NAV_SHORTCUTS.find((s) => s.key === e.key);
        clearG();
        if (match) {
          e.preventDefault();
          navigate(match.path);
        }
        return;
      }

      if (e.key === 'g') {
        gPressed = true;
        gTimer = window.setTimeout(clearG, 1000);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      clearG();
    };
  }, [navigate, onShowHelp]);
}
